import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  XMarkIcon,
  ChevronDownIcon,
  ClipboardIcon,
  CpuChipIcon,
  PencilIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  CheckIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

// TypeScript interfaces
interface OpportunityData {
  id: number
  title: string
  content: string
  content_full: string
  subreddit: string
  author: string
  post_url: string
}

interface ProjectData {
  id: number
  name: string
  business_goal: string
  target_url: string | null
  product_description: string | null
  tone: string | null
  promotional_percentage: number
}

interface TemplateData {
  id: number
  name: string
  tone: string
  style: string
  category: string
  template_text: string
}

interface PromptsData {
  system_prompt: string
  user_prompt: string
  full_prompt: string
}

interface AISettings {
  provider: string
  fallback_provider: string
  model: string
  max_tokens: number
  temperature: number
  presence_penalty: number
  frequency_penalty: number
  estimated_cost: number
}

interface MetadataData {
  prompt_tokens_estimate: number
  system_tokens: number
  total_tokens_estimate: number
  estimated_cost: number
}

export interface PromptPreviewData {
  input_data: {
    opportunity: OpportunityData
    project: ProjectData
    template: TemplateData | null
    custom_context: string
  }
  prompts: PromptsData
  ai_settings: AISettings
  metadata: MetadataData
}

interface PromptPreviewProps {
  previewData: PromptPreviewData
  onClose: () => void
  onConfirm: (editedPrompt?: string, customContext?: string) => void
  isGenerating?: boolean
}

export const PromptPreview: React.FC<PromptPreviewProps> = ({
  previewData,
  onClose,
  onConfirm,
  isGenerating = false
}) => {
  // State for collapsible sections
  const [showSystemPrompt, setShowSystemPrompt] = useState(true)
  const [showUserPrompt, setShowUserPrompt] = useState(true)
  const [showAISettings, setShowAISettings] = useState(false)

  // State for editing
  const [customContext, setCustomContext] = useState(previewData.input_data.custom_context || '')
  const [editedUserPrompt, setEditedUserPrompt] = useState(previewData.prompts.user_prompt)
  const [isEditingPrompt, setIsEditingPrompt] = useState(false)

  // Copy states
  const [copiedSection, setCopiedSection] = useState<string | null>(null)

  const copyToClipboard = (text: string, section: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedSection(section)
      toast.success(`${section} copied to clipboard!`)
      setTimeout(() => setCopiedSection(null), 2000)
    }).catch(() => {
      toast.error('Failed to copy to clipboard')
    })
  }

  const handleConfirm = () => {
    const finalContext = customContext.trim() || undefined
    const finalPrompt = isEditingPrompt && editedUserPrompt !== previewData.prompts.user_prompt
      ? editedUserPrompt
      : undefined

    onConfirm(finalPrompt, finalContext)
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <DocumentTextIcon className="h-6 w-6 mr-2 text-blue-600" />
                Prompt Preview
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Review and edit what will be sent to OpenAI
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isGenerating}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">

          {/* Input Data Summary */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
              <DocumentTextIcon className="h-5 w-5 mr-2" />
              Input Data Summary
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-blue-700 font-medium">Opportunity:</span>
                <p className="text-blue-900 mt-1 truncate" title={previewData.input_data.opportunity.title}>
                  {previewData.input_data.opportunity.title}
                </p>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Subreddit:</span>
                <p className="text-blue-900 mt-1">r/{previewData.input_data.opportunity.subreddit}</p>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Project:</span>
                <p className="text-blue-900 mt-1">{previewData.input_data.project.name}</p>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Business Goal:</span>
                <p className="text-blue-900 mt-1">{previewData.input_data.project.business_goal}</p>
              </div>
              {previewData.input_data.project.target_url && (
                <div className="col-span-1 md:col-span-2">
                  <span className="text-blue-700 font-medium">Target URL:</span>
                  <p className="text-blue-900 mt-1 truncate">
                    {previewData.input_data.project.target_url}
                  </p>
                </div>
              )}
              {previewData.input_data.template && (
                <div className="col-span-1 md:col-span-2">
                  <span className="text-blue-700 font-medium">Template:</span>
                  <p className="text-blue-900 mt-1">
                    {previewData.input_data.template.name} ({previewData.input_data.template.tone} / {previewData.input_data.template.style})
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* System Prompt Section */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowSystemPrompt(!showSystemPrompt)}
              className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center">
                <CpuChipIcon className="h-5 w-5 mr-2 text-gray-600" />
                <span className="font-semibold text-gray-900">System Prompt</span>
                <span className="ml-2 text-xs text-gray-500">
                  (Sets AI behavior - {previewData.metadata.system_tokens} tokens)
                </span>
              </div>
              <ChevronDownIcon
                className={`h-5 w-5 text-gray-400 transition-transform ${
                  showSystemPrompt ? 'transform rotate-180' : ''
                }`}
              />
            </button>
            <AnimatePresence>
              {showSystemPrompt && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 bg-white">
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono bg-gray-50 p-3 rounded border border-gray-200">
                      {previewData.prompts.system_prompt}
                    </pre>
                    <button
                      onClick={() => copyToClipboard(previewData.prompts.system_prompt, 'System prompt')}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-700 flex items-center transition-colors"
                    >
                      {copiedSection === 'System prompt' ? (
                        <>
                          <CheckIcon className="h-4 w-4 mr-1 text-green-600" />
                          <span className="text-green-600">Copied!</span>
                        </>
                      ) : (
                        <>
                          <ClipboardIcon className="h-4 w-4 mr-1" />
                          Copy System Prompt
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* User Prompt Section */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowUserPrompt(!showUserPrompt)}
              className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center">
                <PencilIcon className="h-5 w-5 mr-2 text-gray-600" />
                <span className="font-semibold text-gray-900">User Prompt</span>
                <span className="ml-2 text-xs text-gray-500">
                  ({previewData.metadata.prompt_tokens_estimate} tokens estimated)
                </span>
              </div>
              <ChevronDownIcon
                className={`h-5 w-5 text-gray-400 transition-transform ${
                  showUserPrompt ? 'transform rotate-180' : ''
                }`}
              />
            </button>
            <AnimatePresence>
              {showUserPrompt && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 bg-white">
                    {isEditingPrompt ? (
                      <>
                        <textarea
                          value={editedUserPrompt}
                          onChange={(e) => setEditedUserPrompt(e.target.value)}
                          rows={15}
                          className="w-full px-3 py-2 border border-blue-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                        />
                        <div className="mt-2 flex space-x-2">
                          <button
                            onClick={() => {
                              setEditedUserPrompt(previewData.prompts.user_prompt)
                              setIsEditingPrompt(false)
                            }}
                            className="text-sm text-gray-600 hover:text-gray-700 px-3 py-1 border border-gray-300 rounded"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => setIsEditingPrompt(false)}
                            className="text-sm text-blue-600 hover:text-blue-700 px-3 py-1 border border-blue-300 rounded"
                          >
                            Save Changes
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono bg-gray-50 p-3 rounded border border-gray-200 max-h-96 overflow-y-auto">
                          {editedUserPrompt}
                        </pre>
                        <div className="mt-2 flex space-x-3">
                          <button
                            onClick={() => copyToClipboard(editedUserPrompt, 'User prompt')}
                            className="text-sm text-blue-600 hover:text-blue-700 flex items-center transition-colors"
                          >
                            {copiedSection === 'User prompt' ? (
                              <>
                                <CheckIcon className="h-4 w-4 mr-1 text-green-600" />
                                <span className="text-green-600">Copied!</span>
                              </>
                            ) : (
                              <>
                                <ClipboardIcon className="h-4 w-4 mr-1" />
                                Copy
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => setIsEditingPrompt(true)}
                            className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center transition-colors"
                          >
                            <PencilIcon className="h-4 w-4 mr-1" />
                            Edit Prompt
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* AI Settings Section */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowAISettings(!showAISettings)}
              className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center">
                <Cog6ToothIcon className="h-5 w-5 mr-2 text-gray-600" />
                <span className="font-semibold text-gray-900">AI Settings</span>
              </div>
              <ChevronDownIcon
                className={`h-5 w-5 text-gray-400 transition-transform ${
                  showAISettings ? 'transform rotate-180' : ''
                }`}
              />
            </button>
            <AnimatePresence>
              {showAISettings && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 bg-white">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Provider:</span>
                        <p className="font-medium text-gray-900">{previewData.ai_settings.provider}</p>
                        <span className="text-xs text-gray-500">
                          Fallback: {previewData.ai_settings.fallback_provider}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Model:</span>
                        <p className="font-medium text-gray-900">{previewData.ai_settings.model}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Max Tokens:</span>
                        <p className="font-medium text-gray-900">{previewData.ai_settings.max_tokens}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Temperature:</span>
                        <p className="font-medium text-gray-900">{previewData.ai_settings.temperature}</p>
                        <span className="text-xs text-gray-500">(0 = deterministic, 1 = creative)</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Presence Penalty:</span>
                        <p className="font-medium text-gray-900">{previewData.ai_settings.presence_penalty}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Frequency Penalty:</span>
                        <p className="font-medium text-gray-900">{previewData.ai_settings.frequency_penalty}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Custom Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              💡 Custom Instructions (Optional)
            </label>
            <textarea
              value={customContext}
              onChange={(e) => setCustomContext(e.target.value)}
              placeholder="Add specific instructions for this comment (e.g., 'Focus on time-saving benefits', 'Don't mention pricing')"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isGenerating}
            />
            <p className="mt-1 text-xs text-gray-500">
              These instructions will be appended to the prompt above
            </p>
          </div>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-sm text-gray-600 flex flex-col sm:flex-row sm:items-center gap-2">
            <span>
              Total tokens: <span className="font-semibold">~{previewData.metadata.total_tokens_estimate}</span>
            </span>
            <span className="hidden sm:inline">•</span>
            <span>
              Estimated cost: <span className="font-semibold">${previewData.ai_settings.estimated_cost.toFixed(5)}</span>
            </span>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={isGenerating}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={() => copyToClipboard(previewData.prompts.full_prompt, 'Full prompt')}
              disabled={isGenerating}
              className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
            >
              {copiedSection === 'Full prompt' ? (
                <>
                  <CheckIcon className="h-4 w-4 mr-1" />
                  Copied!
                </>
              ) : (
                <>
                  <ClipboardIcon className="h-4 w-4 mr-1" />
                  Copy Full Prompt
                </>
              )}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isGenerating}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
            >
              {isGenerating ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : (
                'Generate with AI'
              )}
            </button>
          </div>
        </div>

      </motion.div>
    </div>
  )
}

export default PromptPreview
